<script setup lang="ts">
import { computed, toRef } from "vue";

import { cardActions } from "@/action/cardActions";
import ActionMenuDropdown from "@/components/ActionMenu/ActionMenuDropdown.vue";
import { boardKey } from "@/components/board/injectKeys";
import type { BoardCard } from "@/model/card";
import { injectStrict } from "@/utils/vue";

import ActionConfirmModal from "../ActionConfirmModal.vue";
import BulkActionModal from "../BulkActionModal.vue";
import BlockedGroup from "../internal/BlockedGroup.vue";
import BulkActionGroup from "../internal/BulkActionGroup.vue";
import { useBulkActionTracking } from "../useBulkActionTracking";
import { useBulkSubtitle, useRemoveEligibility } from "../useEligibility";

const props = defineProps<{ cards: BoardCard[] }>();
const emit = defineEmits<{ remove: [cards: BoardCard[]] }>();

const board = injectStrict(boardKey);
const cards = toRef(props, "cards");
const { shouldRender, removable, notRemovableUnplannable, notRemovableOrigin } =
  useRemoveEligibility(cards, board);

const single = computed(() => cards.value.length === 1);
const totalCount = computed(
  () =>
    removable.value.length + notRemovableUnplannable.value.length + notRemovableOrigin.value.length,
);
const hasBlocked = computed(
  () => notRemovableUnplannable.value.length > 0 || notRemovableOrigin.value.length > 0,
);
const removableCount = computed(() => removable.value.length);
const subtitle = useBulkSubtitle(
  totalCount,
  removableCount,
  /*$t*/ "action.bulkRemove.subtitlePartial",
);

const { trackOpened, trackConfirmed } = useBulkActionTracking("remove", cards, [
  ["not-removable-unplannable", notRemovableUnplannable],
  ["not-removable-origin", notRemovableOrigin],
]);

function onRemove() {
  trackConfirmed();
  emit("remove", removable.value);
}
</script>

<template>
  <ActionMenuDropdown
    v-if="shouldRender"
    :action="cardActions.remove"
    width="auto"
    max-height="auto"
    :disable-teleport="single"
    @open="trackOpened"
  >
    <ActionConfirmModal
      v-if="single"
      :title="$t('action.removeSticky', 1)"
      :description="$t('action.removeSticky.description', 1)"
      :primary-label="$t('cardAction.remove')"
      primary-icon="general/remove"
      primary-test-id="remove-button"
      @primary="onRemove"
    />
    <BulkActionModal
      v-else
      :title="$t('action.bulkRemove.title')"
      :subtitle="subtitle"
      :primary-label="$t('action.bulkRemove.primary', { count: removable.length })"
      primary-icon="general/remove"
      :primary-disabled="removable.length === 0"
      @primary="onRemove"
    >
      <BulkActionGroup :heading="$t('action.bulkRemove.willBeRemovedHeading')" :cards="removable" />

      <template v-if="hasBlocked" #blocked>
        <BlockedGroup
          :title="$t('action.bulkRemove.blockedUnplannable.title')"
          :description="$t('action.bulkRemove.blockedUnplannable.description')"
          :cards="notRemovableUnplannable"
        />
        <BlockedGroup
          :title="$t('action.bulkRemove.blockedOrigin.title')"
          :description="$t('action.bulkRemove.blockedOrigin.description')"
          :cards="notRemovableOrigin"
        />
      </template>
    </BulkActionModal>
  </ActionMenuDropdown>
</template>
